import React, { useEffect, useState } from 'react';
import Button from '@sabre/spark-react-core/button';
import Icon from '@sabre/spark-react-core/icon';
import Modal from '@sabre/spark-react-core/modal';
import SelectInput from '@sabre/spark-react-core/select-input';
import TextInput from '@sabre/spark-react-core/text-input';
import { func, string } from 'prop-types';
import { ToastContainer } from 'react-toastify';
import { URL_DELETE_BULK } from '../../../../v2/end-points.js';
import { useCreateRecord } from '../../../hooks/use-create-record.js';
import { useDelete } from '../../../hooks/use-delete.js';
import { useSave } from '../../../hooks/use-save.js';
import AgGridReactTable from '../../../ui-components/ag-gird-table/ag-grid-react-table.jsx';
import {
   CAMEL_CASE_TYPES,
   STORED_CRITERIA_CONFIG,
   CAMEL_CASE_ACTION,
   API_STATUS,
} from '../../../utils/constants.js';
import { NOTIFICATION_MESSAGES } from '../../../utils/notification-messages.js';
import { getStoredCriteriaUrl } from '../../../utils/stored-criteria.js';
import { toastSuccess, toastWarn } from '../../../utils/toasts.js';
import {
   parsingData,
   searchFilter,
   findEditRecord,
   filterRecord,
   saveRequestParsing,
   updateSavedRecord,
   setFavouriteFirst,
   findAllEditedRecord,
   updateAllEditRecord,
   scheduleDetailsParsing,
   parsingScheduleDetails,
} from './__helpers__/functions.js';
import { STORED_CRITERIA_TABLE_HEADERS } from './__helpers__/tableHeaders.js';
import ScheduleDetails from './schedule-details.jsx';

const StoredCriteria = ({
   selectedScreen,
   currentUser,
   handleSelectStoredCriteria,
   handleClosePopup,
}) => {
   const [rowData, setRowData] = useState([]);
   const [defaultRowData, setDefaultRowData] = useState([]);
   const [selectedRowIndex, setSelectedRowIndex] = useState(0);
   const [openDeleteConformationPopup, setOpenDeleteConformationPopup] =
      useState(false);
   const [openSaveConformationPopup, setOpenSaveConformationPopup] =
      useState(false);
   const [openSchedulerPopup, setOpenSchedulerPopup] = useState(false);
   const [scheduleRecord, setScheduleRecord] = useState({});

   const [userList, setUserList] = useState({
      rowData: [],
      selectedUserName: '',
   });
   const [isSingleSave, setIsSingleSave] = useState(true);
   const [gridApiRef, setGridApiRef] = useState(null);
   const { createResponseData, createStatus, createApi } = useCreateRecord({});
   const { deleteStatus, deleteChanges } = useDelete({});
   const { saveStatus, saveData } = useSave({});
   useEffect(() => {
      createApi(getStoredCriteriaUrl(selectedScreen, currentUser));
   }, []);

   useEffect(() => {
      if (
         createStatus === API_STATUS.COMPLETED &&
         createResponseData?.userSearch?.length > 0
      ) {
         const users = parsingData(createResponseData.userSearch);
         setUserList({
            rowData: users,
            selectedUserName: users[0].value,
         });
         const favouriteFirst = setFavouriteFirst(
            createResponseData.userSearch
         );
         parsingScheduleDetails(favouriteFirst);
         setRowData(favouriteFirst);
         setDefaultRowData(favouriteFirst);
      }
   }, [createResponseData, createStatus]);

   useEffect(() => {
      if (deleteStatus === API_STATUS.COMPLETED && findSelectedRecord()?.id) {
         const updatedRowData = filterRecord(findSelectedRecord()?.id, rowData);
         setRowData(updatedRowData);
         setOpenDeleteConformationPopup(false);
         toastSuccess(NOTIFICATION_MESSAGES.RECORD_DELETE_SUCCESS);
      }
   }, [deleteStatus]);

   useEffect(() => {
      if (
         saveStatus === API_STATUS.SUCCESS &&
         findSelectedRecord()?.isEdited &&
         isSingleSave
      ) {
         const selectedRecord = findSelectedRecord();
         delete selectedRecord.isEdited;
         const updatedRowData = updateSavedRecord(selectedRecord, rowData);
         setRowData(updatedRowData);
         toastSuccess(NOTIFICATION_MESSAGES.RECORD_UPDATE_SUCCESS);
      } else if (
         saveStatus === API_STATUS.SUCCESS &&
         !isSingleSave &&
         findEditRecord(rowData)
      ) {
         const updatedRowData = updateAllEditRecord(rowData);
         setRowData(updatedRowData);
         setOpenSaveConformationPopup(false);
      }
   }, [saveStatus, isSingleSave]);

   const handleUserNameChange = (userName) => {
      setUserList({
         ...userList,
         selectedUserName: userName,
      });
   };
   const handleSearchFilterChange = (searchQueryName) => {
      const filterRowData = searchQueryName
         ? searchFilter(searchQueryName, defaultRowData)
         : defaultRowData;
      setRowData(filterRowData);
   };

   const handleSelectedRowsChange = (selectedNode) => {
      if (selectedNode) setSelectedRowIndex(selectedNode[0]?.rowIndex);
   };

   const editValidation = () => rowData[selectedRowIndex]?.isEdited;

   const recordAvailability = () => (rowData?.length > 0 ? true : false);

   const handleDeleteRecord = () => {
      if (!recordAvailability()) {
         toastWarn(NOTIFICATION_MESSAGES.NO_RECORD_MESSAGE);
         return;
      }
      if (!editValidation()) {
         setOpenDeleteConformationPopup(true);
      } else toastWarn(NOTIFICATION_MESSAGES.CHANGES_NOT_SAVED);
   };

   const handleSaveRecord = () => {
      if (!recordAvailability()) {
         toastWarn(NOTIFICATION_MESSAGES.NO_RECORD_MESSAGE);
         return;
      }
      if (editValidation()) {
         const saveRequest = saveRequestParsing([findSelectedRecord()]);
         setIsSingleSave(true);
         saveData(saveRequest, URL_DELETE_BULK, 'put');
      } else toastWarn(NOTIFICATION_MESSAGES.RECORD_NOT_UPDATED);
   };

   const handleSaveAllRecord = () => {
      const saveRequest = saveRequestParsing(findAllEditedRecord(rowData));
      setIsSingleSave(false);
      saveData(saveRequest, URL_DELETE_BULK, 'put');
   };

   const handleDiscardAllRecord = () => {
      createApi(getStoredCriteriaUrl(selectedScreen, currentUser));
      setOpenSaveConformationPopup(false);
   };

   const findSelectedRecord = () => {
      const record = rowData[selectedRowIndex];
      return record;
   };

   const handleSelectButtonClick = () => {
      if (!recordAvailability()) {
         toastWarn(NOTIFICATION_MESSAGES.NO_RECORD_MESSAGE);
         return;
      }
      if (!findEditRecord(rowData)) {
         handleSelectStoredCriteria(findSelectedRecord());
      } else toastWarn(NOTIFICATION_MESSAGES.CHANGES_NOT_SAVED);
   };
   const handleCancelButtonClick = () => {
      if (findEditRecord(rowData)) setOpenSaveConformationPopup(true);
      else handleClosePopup();
   };

   const handleDeleteConformationPopupClose = () => {
      setOpenDeleteConformationPopup(false);
   };

   const handleRecordDelete = () => {
      deleteChanges([findSelectedRecord().id], URL_DELETE_BULK);
   };
   const handleSchedulePopupClose = () => {
      setOpenSchedulerPopup(false);
   };
   const handleCallBack = (selectedIndex) => {
      setOpenSchedulerPopup(true);
      setScheduleRecord(rowData[selectedIndex]);
   };
   const handleSelectSchedule = (scheduleDetails) => {
      const updatedStoredCriteriaRowData = scheduleDetailsParsing(
         scheduleDetails,
         rowData,
         selectedRowIndex,
         true
      );
      setRowData(updatedStoredCriteriaRowData);
      gridApiRef.current.refreshCells();
      setOpenSchedulerPopup(false);
      setRowData(updatedStoredCriteriaRowData);
   };
   const getGridApiRef = (gridApiRef) => {
      setGridApiRef(gridApiRef);
   };
   return (
      <div className='stored-criteria-root'>
         {!openSchedulerPopup && (
            <ToastContainer autoClose={3000} hideProgressBar theme='colored' />
         )}
         {openSchedulerPopup && (
            <div className='schedule-popup'>
               <Modal
                  onClose={() => handleSchedulePopupClose()}
                  open={openSchedulerPopup}
                  title={
                     <div className='spark-mar-l-1'>
                        <h3>
                           <strong>Schedule</strong>
                        </h3>
                     </div>
                  }
                  titleHeadingLevel={3}
                  modalId='dashboard-search-modal'
                  className={`dashboard-search-modal${
                     openSchedulerPopup ? '' : ' spark-hidden'
                  } `}
                  contentClassName='col-lg-4'
               >
                  <ScheduleDetails
                     rowData={scheduleRecord.schedule}
                     handleSchedulePopupClose={handleSchedulePopupClose}
                     handleSelectSchedule={handleSelectSchedule}
                  />
               </Modal>
            </div>
         )}
         {openSaveConformationPopup && (
            <div className='core-pricing-delete-popup row'>
               <Modal
                  onClose={() => handleDiscardAllRecord()}
                  open={openSaveConformationPopup}
                  title={
                     <div className='spark-mar-l-1'>
                        <h3>
                           <strong>
                              {STORED_CRITERIA_CONFIG.SAVE_ALL_POPUP_TITLE}
                           </strong>
                        </h3>
                     </div>
                  }
                  titleHeadingLevel={3}
                  modalId='dashboard-search-modal'
                  className={`dashboard-search-modal${
                     openSaveConformationPopup ? '' : ' spark-hidden'
                  } `}
                  contentClassName='col-xs-12 col-sm-12 col-md-11 col-lg-8 col-xl-6'
               >
                  <div>
                     <div className='row col-md-12 col-lg-12'>
                        <strong>
                           {STORED_CRITERIA_CONFIG.SAVE_ALL_POPUP_BODY}
                        </strong>
                     </div>
                     <div className='row popup-button col-md-12 col-lg-12'>
                        <Button
                           className='spark-btn-group-secondary select-button-cancel'
                           size={Button.SIZE.SMALL}
                           secondary={true}
                           onClick={() => handleDiscardAllRecord()}
                        >
                           DISCARD ALL
                        </Button>
                        <Button
                           className='spark-btn-group-primary'
                           size={Button.SIZE.SMALL}
                           primary='true'
                           onClick={() => handleSaveAllRecord()}
                        >
                           SAVE ALL
                        </Button>
                     </div>
                  </div>
               </Modal>
            </div>
         )}
         {openDeleteConformationPopup && (
            <div className='core-pricing-delete-popup row'>
               <Modal
                  onClose={() => handleDeleteConformationPopupClose()}
                  open={openDeleteConformationPopup}
                  title={
                     <div className='spark-mar-l-1'>
                        <h3>
                           <strong>
                              {STORED_CRITERIA_CONFIG.DELETE_POPUP_TITLE}
                           </strong>
                        </h3>
                     </div>
                  }
                  titleHeadingLevel={3}
                  modalId='dashboard-search-modal'
                  className={`dashboard-search-modal${
                     openDeleteConformationPopup ? '' : ' spark-hidden'
                  } `}
                  contentClassName='col-xs-12 col-sm-12 col-md-11 col-lg-8 col-xl-6'
               >
                  <div>
                     <div className='row col-md-12 col-lg-12'>
                        <strong>
                           {STORED_CRITERIA_CONFIG.DELETE_POPUP_BODY}
                        </strong>
                     </div>
                     <div className='row popup-button col-md-12 col-lg-12'>
                        <Button
                           className='spark-btn-group-secondary select-button-cancel'
                           size={Button.SIZE.SMALL}
                           secondary={true}
                           onClick={() => handleDeleteConformationPopupClose()}
                        >
                           CANCEL
                        </Button>
                        <Button
                           className='spark-btn-group-primary'
                           size={Button.SIZE.SMALL}
                           primary='true'
                           onClick={() => handleRecordDelete()}
                        >
                           DELETE
                        </Button>
                     </div>
                  </div>
               </Modal>
            </div>
         )}
         <div className='row'>
            <div
               data-testid='stored-criteria-title'
               className='spark-bold spark-large'
            >
               {STORED_CRITERIA_CONFIG.STORED_CRITERIA_TITLE}
            </div>
         </div>
         <div className='row'>
            <div className=' row col-md-6 col-lg-6'>
               <div
                  className='col-md-4 col-lg-4'
                  data-testid='select-user-title'
               >
                  {STORED_CRITERIA_CONFIG.SELECTED_USER_LABEL}
               </div>
               <div className='col-md-8 col-lg-8'>
                  <SelectInput
                     name={'stored-criteria-dropdown'}
                     options={userList.rowData}
                     value={userList.selectedUserName}
                     data-testid='select-user-drop-down'
                     title='select-user-drop-down'
                     disabled={editValidation()}
                     onChange={(event) =>
                        handleUserNameChange(event.target.value)
                     }
                  />
               </div>
            </div>
            <div className=' row col-md-6 col-lg-6'>
               <div className='col-md-3 col-lg-3' data-testid='search-label'>
                  {CAMEL_CASE_ACTION.SEARCH}
               </div>
               <div className='col-md-7 col-lg-7'>
                  <TextInput
                     data-testid='search-filter-input'
                     name='search-query-name'
                     autoComplete='off'
                     disabled={editValidation()}
                     onChange={(event, selectedValue) =>
                        handleSearchFilterChange(selectedValue)
                     }
                  />
               </div>
            </div>
         </div>
         <div className='row stored-criteria-icons'>
            <div className='stored-criteria-icons-common stored-criteria-icons-delete'>
               <Icon
                  name='trash'
                  className='spark-danger'
                  data-testid='delete-icon'
                  size='md'
                  style='fill'
                  label={CAMEL_CASE_TYPES.DELETE}
                  onClick={() => handleDeleteRecord()}
               />
            </div>
            <div className='stored-criteria-icons-common'>
               <Icon
                  name='save'
                  data-testid='save-icon'
                  className='spark-success'
                  size='md'
                  style='fill'
                  label={CAMEL_CASE_TYPES.SAVE}
                  onClick={() => handleSaveRecord()}
               />
            </div>
         </div>
         <div className='row spark-pad-t-.5 view'>
            <div className='table-root'>
               <AgGridReactTable
                  rowData={rowData}
                  headers={STORED_CRITERIA_TABLE_HEADERS}
                  autoSizeColumns={false}
                  selectFirstRowByDefault
                  enableRowIds
                  rowHeight={21}
                  onSelectedRowsChange={handleSelectedRowsChange}
                  handleClosePopup={handleClosePopup}
                  handleCallBack={handleCallBack}
                  getGridApiRef={getGridApiRef}
               />
            </div>
         </div>
         <div className='row select-button col-md-12 col-lg-12'>
            <nav className='spark-btn-group spark-mar-t-1'>
               <Button
                  name='select-button'
                  className='spark-btn-group-primary'
                  size={Button.SIZE.SMALL}
                  primary={true}
                  onClick={() => handleSelectButtonClick()}
               >
                  SELECT
               </Button>
               <Button
                  name='cancel-button'
                  className='spark-btn-group-secondary select-button-cancel'
                  size={Button.SIZE.SMALL}
                  secondary={true}
                  onClick={() => handleCancelButtonClick()}
               >
                  CANCEL
               </Button>
            </nav>
         </div>
      </div>
   );
};
StoredCriteria.propTypes = {
   currentUser: string,
   selectedScreen: string,
   handleSelectStoredCriteria: func,
   handleClosePopup: func,
};

export default StoredCriteria;
